/**
 * KIEU Styling Game
 * Drag and drop products onto models
 */

class StylingGame {
  constructor() {
    this.currentGender = 'female';
    this.droppedProducts = [];
    this.draggedElement = null;
    this.touchOffset = { x: 0, y: 0 };

    this.init();
  }

  init() {
    this.canvas = document.getElementById('styling-canvas');
    this.droppedContainer = document.getElementById('dropped-products');
    this.productBank = document.getElementById('product-bank');
    this.currentOutfit = document.getElementById('current-outfit');
    this.modelMale = document.getElementById('model-male');
    this.modelFemale = document.getElementById('model-female');

    if (!this.canvas) return;

    this.setupDragAndDrop();
    this.setupGenderToggle();
    this.setupClearButton();
  }

  setupDragAndDrop() {
    // Desktop drag events
    const productItems = document.querySelectorAll('.product-bank__item');

    productItems.forEach(item => {
      // Desktop drag
      item.addEventListener('dragstart', (e) => this.handleDragStart(e));
      item.addEventListener('dragend', (e) => this.handleDragEnd(e));

      // Mobile touch
      item.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: false });
      item.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
      item.addEventListener('touchend', (e) => this.handleTouchEnd(e));
    });

    // Canvas drop zone
    this.canvas.addEventListener('dragover', (e) => e.preventDefault());
    this.canvas.addEventListener('drop', (e) => this.handleDrop(e));
  }

  handleDragStart(e) {
    this.draggedElement = e.target.closest('.product-bank__item');
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/html', this.draggedElement.innerHTML);
    this.draggedElement.classList.add('dragging');
  }

  handleDragEnd(e) {
    if (this.draggedElement) {
      this.draggedElement.classList.remove('dragging');
    }
  }

  handleDrop(e) {
    e.preventDefault();
    if (!this.draggedElement) return;

    const canvasRect = this.canvas.getBoundingClientRect();
    const x = e.clientX - canvasRect.left;
    const y = e.clientY - canvasRect.top;

    this.addProductToCanvas(this.draggedElement, x, y);
    this.draggedElement = null;
  }

  // Touch events for mobile
  handleTouchStart(e) {
    e.preventDefault();
    this.draggedElement = e.target.closest('.product-bank__item');

    const touch = e.touches[0];
    const rect = this.draggedElement.getBoundingClientRect();

    // Calculate offset from touch point to element top-left
    this.touchOffset = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };

    // Create a clone for visual feedback
    const clone = this.draggedElement.cloneNode(true);
    clone.classList.add('dragging-touch');
    clone.style.position = 'fixed';
    clone.style.pointerEvents = 'none';
    clone.style.zIndex = '10000';
    clone.style.opacity = '0.8';
    clone.style.width = rect.width + 'px';
    clone.style.left = (touch.clientX - this.touchOffset.x) + 'px';
    clone.style.top = (touch.clientY - this.touchOffset.y) + 'px';

    this.touchClone = clone;
    document.body.appendChild(clone);
  }

  handleTouchMove(e) {
    e.preventDefault();
    if (!this.touchClone) return;

    const touch = e.touches[0];
    this.touchClone.style.left = (touch.clientX - this.touchOffset.x) + 'px';
    this.touchClone.style.top = (touch.clientY - this.touchOffset.y) + 'px';
  }

  handleTouchEnd(e) {
    if (!this.draggedElement || !this.touchClone) return;

    const touch = e.changedTouches[0];
    const canvasRect = this.canvas.getBoundingClientRect();

    // Check if dropped on canvas
    if (
      touch.clientX >= canvasRect.left &&
      touch.clientX <= canvasRect.right &&
      touch.clientY >= canvasRect.top &&
      touch.clientY <= canvasRect.bottom
    ) {
      const x = touch.clientX - canvasRect.left;
      const y = touch.clientY - canvasRect.top;
      this.addProductToCanvas(this.draggedElement, x, y);
    }

    // Clean up
    if (this.touchClone) {
      document.body.removeChild(this.touchClone);
      this.touchClone = null;
    }
    this.draggedElement = null;
  }

  addProductToCanvas(sourceElement, x, y) {
    const productData = {
      id: sourceElement.dataset.productId,
      handle: sourceElement.dataset.productHandle,
      title: sourceElement.dataset.productTitle,
      url: sourceElement.dataset.productUrl,
      gender: sourceElement.dataset.gender,
      x: x,
      y: y
    };

    // Create dropped product element
    const droppedProduct = document.createElement('div');
    droppedProduct.className = 'dropped-product';
    droppedProduct.dataset.productId = productData.id;

    // Clone the image without the product-bank__image class to avoid size constraints
    const sourceImg = sourceElement.querySelector('.product-bank__image');
    const newImg = document.createElement('img');
    newImg.src = sourceImg.src;
    newImg.alt = sourceImg.alt;
    
    // Add specific class for CSS targeting
    newImg.classList.add('dropped-product-img');

    // IMPORTANT: Remove Width Calculation here. Let CSS handle it.
    // We only keep max-width/height none to override Shopify theme defaults.
    newImg.style.setProperty('max-width', 'none', 'important');
    newImg.style.setProperty('max-height', 'none', 'important');
    newImg.style.height = 'auto';
    
    droppedProduct.appendChild(newImg);

    // Position on canvas
    droppedProduct.style.position = 'absolute';
    droppedProduct.style.left = `${x}px`;
    droppedProduct.style.top = `${y}px`;
    droppedProduct.style.transform = 'translate(-50%, -50%)';
    droppedProduct.style.cursor = 'grab';

    // Make dropped products draggable and removable
    this.makeDroppedProductDraggable(droppedProduct, productData);

    this.droppedContainer.appendChild(droppedProduct);
    this.droppedProducts.push(productData);
    this.updateOutfitList();
  }

  makeDroppedProductDraggable(element, productData) {
    let isDragging = false;
    let startX, startY, initialLeft, initialTop;

    // Desktop drag
    element.addEventListener('mousedown', (e) => {
      isDragging = true;
      element.style.cursor = 'grabbing';
      this.canvas.classList.add('dragging-active');

      const rect = element.getBoundingClientRect();
      const canvasRect = this.canvas.getBoundingClientRect();

      startX = e.clientX;
      startY = e.clientY;
      initialLeft = rect.left + rect.width / 2 - canvasRect.left;
      initialTop = rect.top + rect.height / 2 - canvasRect.top;

      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;

      const canvasRect = this.canvas.getBoundingClientRect();
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      const newLeft = initialLeft + deltaX;
      const newTop = initialTop + deltaY;

      element.style.left = `${newLeft}px`;
      element.style.top = `${newTop}px`;
    });

    document.addEventListener('mouseup', (e) => {
      if (!isDragging) return;
      isDragging = false;
      element.style.cursor = 'grab';
      this.canvas.classList.remove('dragging-active');

      // Check if dragged outside canvas
      const canvasRect = this.canvas.getBoundingClientRect();
      if (
        e.clientX < canvasRect.left ||
        e.clientX > canvasRect.right ||
        e.clientY < canvasRect.top ||
        e.clientY > canvasRect.bottom
      ) {
        this.removeProductFromCanvas(element, productData.id);
      }
    });

    // Touch drag for mobile
    element.addEventListener('touchstart', (e) => {
      isDragging = true;
      this.canvas.classList.add('dragging-active');

      const touch = e.touches[0];
      const rect = element.getBoundingClientRect();
      const canvasRect = this.canvas.getBoundingClientRect();

      startX = touch.clientX;
      startY = touch.clientY;
      initialLeft = rect.left + rect.width / 2 - canvasRect.left;
      initialTop = rect.top + rect.height / 2 - canvasRect.top;

      e.preventDefault();
    }, { passive: false });

    element.addEventListener('touchmove', (e) => {
      if (!isDragging) return;

      const touch = e.touches[0];
      const canvasRect = this.canvas.getBoundingClientRect();
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;

      const newLeft = initialLeft + deltaX;
      const newTop = initialTop + deltaY;

      element.style.left = `${newLeft}px`;
      element.style.top = `${newTop}px`;

      e.preventDefault();
    }, { passive: false });

    element.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      isDragging = false;
      this.canvas.classList.remove('dragging-active');

      const touch = e.changedTouches[0];
      const canvasRect = this.canvas.getBoundingClientRect();

      if (
        touch.clientX < canvasRect.left ||
        touch.clientX > canvasRect.right ||
        touch.clientY < canvasRect.top ||
        touch.clientY > canvasRect.bottom
      ) {
        this.removeProductFromCanvas(element, productData.id);
      }
    });
  }

  removeProductFromCanvas(element, productId) {
    element.remove();
    this.droppedProducts = this.droppedProducts.filter(p => p.id !== productId);
    this.updateOutfitList();
  }

  updateOutfitList() {
    if (this.droppedProducts.length === 0) {
      this.currentOutfit.innerHTML = '<p class="current-outfit__empty">Drag products onto the model to style</p>';
      return;
    }

    const listHTML = this.droppedProducts.map(product => `
      <div class="outfit-item">
        <a href="${product.url}" class="outfit-item__link">
          ${product.title}
        </a>
      </div>
    `).join('');

    this.currentOutfit.innerHTML = listHTML;
  }

  setupGenderToggle() {
    const toggleMale = document.getElementById('toggle-male');
    const toggleFemale = document.getElementById('toggle-female');

    toggleMale?.addEventListener('click', () => this.switchGender('male'));
    toggleFemale?.addEventListener('click', () => this.switchGender('female'));
  }

  switchGender(gender) {
    this.currentGender = gender;

    // Toggle models
    if (gender === 'male') {
      this.modelMale.style.display = 'block';
      this.modelFemale.style.display = 'none';
    } else {
      this.modelMale.style.display = 'none';
      this.modelFemale.style.display = 'block';
    }

    // Toggle product banks
    const categories = document.querySelectorAll('.product-bank__category');
    categories.forEach(cat => {
      if (cat.dataset.gender === gender) {
        // Remove property to allow CSS (flex on mobile, grid on desktop) to take over
        cat.style.removeProperty('display');
      } else {
        cat.style.display = 'none';
      }
    });

    // Update toggle buttons
    document.querySelectorAll('.gender-toggle').forEach(btn => {
      btn.classList.toggle('gender-toggle--active', btn.dataset.gender === gender);
    });

    // Clear canvas when switching
    this.clearCanvas();
  }

  setupClearButton() {
    const clearBtn = document.getElementById('clear-canvas');
    clearBtn?.addEventListener('click', () => this.clearCanvas());
  }

  clearCanvas() {
    this.droppedContainer.innerHTML = '';
    this.droppedProducts = [];
    this.updateOutfitList();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new StylingGame());
} else {
  new StylingGame();
}